import React, { useState, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { ValidatorForm } from "react-material-ui-form-validator";
import axios from "../../../axios-order";
import MonthDatePicker from "../../../components/ui/monthWiseDatePicker";
import Modal from "react-bootstrap/Modal";
import LoadingButton from "@mui/lab/LoadingButton";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@material-ui/lab/Alert";

// ── Design tokens (identical palette to ItcAvailReport) ──────────────────────
const C = {
    navy: "#1a3f6f",
    accent: "#1a6fc4",
    accentLight: "#e8f1fb",
    white: "#ffffff",
    surface: "#f7f9fc",
    border: "#dde3ec",
    borderLight: "#eef1f6",
    text: "#1a3f6f",
    textSub: "#4a6080",
    textMuted: "#8298b2",
    success: { bg: "#edf8f1", text: "#1a6e3e", dot: "#28a55f" },
};

const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function formatPeriod(period) {
    if (!period) return "—";
    // Handle both "042022" format and "Apr-2022" format
    if (period.includes("-") || period.length <= 8 && isNaN(period)) return period;
    const mm = parseInt(period.slice(0, 2)) - 1;
    const yy = period.slice(2);
    return `${monthNames[mm]} ${yy}`;
}

function formatDateTime(dt) {
    if (!dt) return "—";
    const cleaned = dt.replace("Z", "");
    const parts = cleaned.includes("T") ? cleaned.split("T") : cleaned.split(" ");
    const date = parts[0];
    const time = parts[1] || "";
    return (
        <span>
            <span style={{ color: C.text, fontWeight: "500" }}>{date}</span>
            {time && <span style={{ color: C.textMuted }}> · {time}</span>}
        </span>
    );
}

const DocIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="9" y1="13" x2="15" y2="13" />
        <line x1="9" y1="17" x2="13" y2="17" />
    </svg>
);

// ── Component ─────────────────────────────────────────────────────────────────
export default function VendorCancellation() {
    const [handleFromDate, setHandleFromDate] = useState("");
    const [handleToDate, setHandleToDate] = useState("");
    const [handleFromDateToDisplay, setHandleFromDateToDisplay] = useState(null);
    const [handleToDateToDisplay, setHandleToDateToDisplay] = useState(null);
    const [data, setData] = useState([]);
    const [isExportButtonVisible, setIsExportButtonVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [trigarLoader, setTrigarLoader] = useState(false);
    const [trigarForErrorMessage, setTrigarForErrorMessage] = useState(false);
    const [triggerButton] = useState(true);
    const [triggerModelFromToDate, setTriggerModelFromToDate] = useState(false);
    const [loaderSubmitButton, setLoaderSubmitButton] = useState(false);
    const [datesSubmitted, setDatesSubmitted] = useState(false);
    const [hoveredRow, setHoveredRow] = useState(null);

    // Snackbar state
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    const history = useHistory();
    const location = useLocation();

    const handleSnackbarClose = () => setSnackbarOpen(false);
    const handleBack = () => history.push("/reportCards");

    // ── Data fetch ────────────────────────────────────────────────────────────
    const fetchLogs = () => {
        const business_entity_id = localStorage.getItem("business_entity_id");
        setLoading(true);
        axios
            .get(`/blocked_gstin/get_logs/${business_entity_id}`)
            .then((res) => {
                if (res.data.status) {
                    setData(res.data.records || []);
                    setIsExportButtonVisible(true);
                } else {
                    setSnackbarMessage(res.data.message);
                    setSnackbarSeverity("error");
                    setSnackbarOpen(true);
                    setIsExportButtonVisible(false);
                }
            })
            .catch((err) => {
                const errorMessage = err.response?.data?.message || "An unexpected error occurred";
                setSnackbarMessage(errorMessage);
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchLogs(); }, []);

    // ── Date handlers ─────────────────────────────────────────────────────────
    const functionToHandleFromDate = (v) => {
        const pad = "00";
        const yy = v.getFullYear().toString();
        const mm = (v.getMonth() + 1).toString();
        setHandleFromDate(`${(pad + mm).slice(-2)}${yy}`);
        setHandleFromDateToDisplay(v);
    };

    const functionToHandleToDate = (v) => {
        const pad = "00";
        const year = v.getFullYear().toString();
        const month = (v.getMonth() + 1).toString();
        setHandleToDate(`${(pad + month).slice(-2)}${year}`);
        setHandleToDateToDisplay(v);
    };

    // ── Modal open/close ──────────────────────────────────────────────────────
    const handleOpenAndCloseModel = () => {
        setHandleFromDateToDisplay(null);
        setHandleToDateToDisplay(null);
        setTriggerModelFromToDate(false);
        setTrigarLoader(false);
        setTrigarForErrorMessage(false);
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const onSubmitHandlerForData = () => {
        setDatesSubmitted(true);
        setLoading(true);
        setLoaderSubmitButton(true);

        const startPeriod = handleFromDate || localStorage.getItem("start_period");
        const endPeriod = handleToDate || localStorage.getItem("end_period");

        const payLoadData = {
            business_entity_id: localStorage.getItem("business_entity_id"),
            start_period: startPeriod,
            end_period: endPeriod,
            org_gstin: localStorage.getItem("gst"),
            vendor_gstin: localStorage.getItem("vendor_gstin"),
        };

        axios
            .post("/blocked_gstin/save_logs", payLoadData)
            .then((res) => {
                if (res.data.status) {
                    setSnackbarMessage(res.data.message);
                    setSnackbarSeverity("success");
                    setData(res.data.records);
                    setIsExportButtonVisible(true);
                    fetchLogs();
                } else {
                    setSnackbarMessage(res.data.message);
                    setSnackbarSeverity("error");
                    setData([]);
                    setIsExportButtonVisible(false);
                }
                setSnackbarOpen(true);
                handleOpenAndCloseModel();
            })
            .catch((err) => {
                const errorMessage = err.response?.data?.message || "An unexpected error occurred";
                setSnackbarMessage(errorMessage);
                setSnackbarSeverity("error");
                setSnackbarOpen(true);
                setData([]);
                handleOpenAndCloseModel();
                setIsExportButtonVisible(false);
                fetchLogs();
            })
            .finally(() => {
                setLoading(false);
                setLoaderSubmitButton(false);
            });
    };

    const handleViewData = (id) => {
        localStorage.setItem("record_id", id);
        history.push("/VendorViewData");
    };

    const handleTDSREconciliationButtonEvent = () => setTriggerModelFromToDate(true);

    // ── Styles ────────────────────────────────────────────────────────────────
    const thStyle = {
        padding: "11px 18px",
        textAlign: "left",
        fontSize: "11px",
        fontWeight: "600",
        letterSpacing: "0.05em",
        color: "#ffffff",
        background: C.navy,
        borderBottom: "1px solid #16355c",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
    };

    const tdStyle = {
        padding: "8px 18px",
        fontSize: "13px",
        color: C.text,
        borderBottom: `1px solid ${C.borderLight}`,
    };

    // ── Table body renderer ───────────────────────────────────────────────────
    const renderTableBody = () => {
        if (loading) {
            return Array.from({ length: 3 }).map((_, i) => (
                <tr key={i}>
                    {[25, 20, 30, 15, 10].map((w, j) => (
                        <td key={j} style={tdStyle}>
                            <div style={{ height: "14px", borderRadius: "4px", background: C.borderLight, width: `${w}%`, animation: "pulse 1.4s ease-in-out infinite" }} />
                        </td>
                    ))}
                </tr>
            ));
        }

        if (!data || data.length === 0) {
            return (
                <tr>
                    <td colSpan={5} style={{ ...tdStyle, textAlign: "center", padding: "52px 20px" }}>
                        <div style={{ fontSize: "28px", marginBottom: "10px", opacity: 0.4 }}>📄</div>
                        <div style={{ fontWeight: "500", marginBottom: "4px", color: C.textSub }}>No reports found</div>
                        <div style={{ fontSize: "12px", color: C.textMuted }}>
                            Generate your first Vendor Cancellation report to get started.
                        </div>
                    </td>
                </tr>
            );
        }

        return data.map((record, i) => (
            <tr
                key={i}
                onMouseEnter={() => setHoveredRow(i)}
                onMouseLeave={() => setHoveredRow(null)}
                style={{ background: hoveredRow === i ? C.surface : C.white, transition: "background 0.1s" }}
            >
                {/* From Period */}
                <td style={tdStyle}>
                    <span style={{ fontWeight: "600" }}>{formatPeriod(record.from_tax_period)}</span>
                </td>
                {/* To Period */}
                <td style={tdStyle}>
                    <span style={{ fontWeight: "600" }}>{formatPeriod(record.to_tax_period)}</span>
                </td>
                {/* Run Date Time */}
                <td style={tdStyle}>
                    <span style={{ fontSize: "12px", color: C.textMuted, fontVariantNumeric: "tabular-nums" }}>
                        {formatDateTime(record.run_date_time)}
                    </span>
                </td>
                {/* Status */}
                {/* Status */}
                <td style={tdStyle}>
                    {(() => {
                        const s = record.report_status;
                        const scheme =
                            s === "Generated" ? { bg: "#edf8f1", text: "#1a6e3e", dot: "#28a55f" } :
                                s === "Failed" ? { bg: "#fef2f2", text: "#991b1b", dot: "#ef4444" } :
                                    s === "No Data" ? { bg: "#fffbeb", text: "#7a4e00", dot: "#f0a500" } :
                                        s === "In Progress" ? { bg: "#eff6ff", text: "#1e40af", dot: "#3b82f6" } :
                                            s === "Initiated" ? { bg: "#eff6ff", text: "#1e40af", dot: "#3b82f6" } :
                                                { bg: "#f3f4f6", text: "#6b7280", dot: "#9ca3af" };
                        return (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: "5px", padding: "3px 10px", borderRadius: "5px", fontSize: "11px", fontWeight: "600", background: scheme.bg, color: scheme.text }}>
                                <span style={{ width: "6px", height: "6px", borderRadius: "50%", background: scheme.dot, flexShrink: 0 }} />
                                {s ? s.toUpperCase() : "GENERATED"}
                            </span>
                        );
                    })()}
                </td>
                {/* Action */}
                {/* Action */}
                <td style={{ ...tdStyle, textAlign: "center" }}>
                    <button
                        onClick={() => {
                            if (record.report_status === "Generated") {
                                handleViewData(record.id);
                            } else if (record.report_status === "Failed") {
                                setSnackbarMessage(record.remarks || "Report generation failed.");
                                setSnackbarSeverity("error");
                                setSnackbarOpen(true);
                            } else if (record.report_status === "No Data") {
                                setSnackbarMessage(record.remarks || "No data found for the selected period.");
                                setSnackbarSeverity("warning");
                                setSnackbarOpen(true);
                            }
                        }}
                        disabled={record.report_status === "Initiated" || record.report_status === "In Progress"}
                        style={{
                            padding: "5px 14px",
                            borderRadius: "6px",
                            whiteSpace: "nowrap",
                            border: `1px solid ${record.report_status === "Initiated" || record.report_status === "In Progress"
                                ? C.borderLight
                                : hoveredRow === i ? C.accent : C.border
                                }`,
                            background: record.report_status === "Initiated" || record.report_status === "In Progress"
                                ? C.surface
                                : hoveredRow === i ? C.accentLight : C.white,
                            color: record.report_status === "Initiated" || record.report_status === "In Progress"
                                ? C.textMuted
                                : hoveredRow === i ? C.accent : C.textSub,
                            fontSize: "12px",
                            fontWeight: "500",
                            cursor: record.report_status === "Initiated" || record.report_status === "In Progress"
                                ? "not-allowed"
                                : "pointer",
                            transition: "all 0.15s",
                            opacity: record.report_status === "Initiated" || record.report_status === "In Progress" ? 0.5 : 1
                        }}
                    >
                        View report →
                    </button>
                </td>
            </tr>
        ));
    };

    const totalCount = data ? data.length : 0;
    const generatedCount = data ? data.filter(r => r.report_status).length : 0;
    const periodsCount = data ? new Set(data.map(r => r.from_tax_period)).size : 0;

    return (
        <React.Fragment>
            <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>

            <div style={{ padding: "28px 32px", background: C.surface, fontFamily: "inherit" }}>

                {/* ── Page header ── */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: C.white, border: `1px solid ${C.border}`, borderRadius: "10px", padding: "16px 20px", marginBottom: "20px", boxShadow: "0 1px 3px rgba(13,33,55,0.06)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                        <button
                            onClick={handleBack}
                            title="Back to Reports"
                            style={{ width: "32px", height: "32px", borderRadius: "8px", border: `1px solid ${C.border}`, background: C.white, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: C.textSub, fontSize: "14px", flexShrink: 0 }}
                        >←</button>
                        <div style={{ width: "42px", height: "42px", borderRadius: "10px", background: C.accentLight, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <DocIcon />
                        </div>
                        <div>
                            <div style={{ fontSize: "23px", fontWeight: "700", color: C.text, lineHeight: 1.2 }}>Vendor Cancellation Report</div>
                            <div style={{ fontSize: "12px", color: C.textMuted, marginTop: "4px" }}>Blocked GSTIN vendor cancellation records</div>
                        </div>
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                        <button
                            onClick={fetchLogs}
                            style={{ display: "flex", alignItems: "center", gap: "6px", padding: "0 16px", height: "36px", borderRadius: "8px", background: C.white, color: C.textSub, border: `1px solid ${C.border}`, fontSize: "13px", fontWeight: "500", cursor: "pointer" }}
                        >
                            ↻ Refresh
                        </button>
                        {triggerButton && (
                            <button
                                onClick={handleTDSREconciliationButtonEvent}
                                style={{ display: "flex", alignItems: "center", gap: "6px", padding: "0 18px", height: "36px", borderRadius: "8px", background: C.navy, color: C.white, border: "none", fontSize: "13px", fontWeight: "500", cursor: "pointer" }}
                            >
                                <span style={{ fontSize: "16px", lineHeight: 1 }}>+</span>
                                New Vendor Cancellation Report
                            </button>
                        )}
                    </div>
                </div>

                {/* ── Metrics cards ── */}
                {/* <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "14px", marginBottom: "20px" }}>
                    {[
                        { label: "Total Reports", value: totalCount, borderColor: C.accent, badge: totalCount > 0 },
                        { label: "Generated", value: generatedCount, borderColor: "#28a55f", badge: false },
                        { label: "Periods Covered", value: periodsCount, borderColor: "#8298b2", badge: false },
                    ].map(({ label, value, borderColor, badge }) => (
                        <div key={label} style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: "10px", padding: "16px 20px", boxShadow: "0 1px 3px rgba(13,33,55,0.05)", borderTop: `3px solid ${borderColor}` }}>
                            <div style={{ fontSize: "26px", fontWeight: "700", color: C.text, lineHeight: 1, letterSpacing: "-0.5px" }}>{value}</div>
                            <div style={{ fontSize: "11px", color: C.textMuted, marginTop: "5px", fontWeight: "500", textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
                            {badge && <div style={{ display: "inline-block", marginTop: "8px", fontSize: "10px", padding: "2px 8px", borderRadius: "12px", background: C.success.bg, color: C.success.text, fontWeight: "600" }}>All generated</div>}
                        </div>
                    ))}
                </div> */}

                {/* ── Table card ── */}
                <div style={{ background: C.white, border: `1px solid ${C.border}`, borderRadius: "10px", overflow: "hidden", boxShadow: "0 1px 3px rgba(13,33,55,0.05)" }}>

                    {/* Scrollable table */}
                    <div style={{ maxHeight: "480px", overflowY: "auto", overflowX: "hidden" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
                            <colgroup>
                                <col style={{ width: "18%" }} />
                                <col style={{ width: "18%" }} />
                                <col style={{ width: "30%" }} />
                                <col style={{ width: "20%" }} />
                                <col style={{ width: "14%" }} />
                            </colgroup>
                            <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                                <tr>
                                    <th style={thStyle}>From Period</th>
                                    <th style={thStyle}>To Period</th>
                                    <th style={thStyle}>Run Date &amp; Time</th>
                                    <th style={thStyle}>Status</th>
                                    <th style={{ ...thStyle, textAlign: "center" }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>{renderTableBody()}</tbody>
                        </table>
                    </div>

                    {/* Footer */}
                    {!loading && data && data.length > 0 && (
                        <div style={{ padding: "10px 18px", borderTop: `1px solid ${C.borderLight}`, fontSize: "11px", color: C.textMuted, background: C.white }}>
                            Showing {data.length} record{data.length !== 1 ? "s" : ""}
                        </div>
                    )}
                </div>

            </div>

            {/* ── Modal ── */}
            <Modal size="lg" centered show={triggerModelFromToDate} onHide={handleOpenAndCloseModel}>
                <Modal.Header closeButton style={{ background: C.navy, borderBottom: "none", padding: "16px 22px" }}>
                    <Modal.Title style={{ fontSize: "18px", fontWeight: "600", color: "#ffffff" }}>
                        Generate Vendor Cancellation Report
                    </Modal.Title>
                </Modal.Header>

                <ValidatorForm onSubmit={onSubmitHandlerForData}>
                    <Modal.Body style={{ padding: "22px 22px 10px" }}>
                        <div style={{ marginBottom: "18px" }}>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}>
                                <div>
                                    <label style={{ fontSize: "12px", fontWeight: "600", color: C.textSub, marginBottom: "6px", display: "block", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                        From Month <span style={{ color: "#e53e3e" }}>*</span>
                                    </label>
                                    <MonthDatePicker
                                        placeholder="Month & Year"
                                        validators={["required"]}
                                        value={handleFromDateToDisplay}
                                        onChange={(v) => functionToHandleFromDate(v)}
                                    />
                                </div>
                                <div>
                                    <label style={{ fontSize: "12px", fontWeight: "600", color: C.textSub, marginBottom: "6px", display: "block", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                        To Month <span style={{ color: "#e53e3e" }}>*</span>
                                    </label>
                                    <MonthDatePicker
                                        placeholder="Month & Year"
                                        minDateForDatepicker={handleFromDateToDisplay}
                                        validators={["required"]}
                                        value={handleToDateToDisplay}
                                        onChange={(v) => functionToHandleToDate(v)}
                                    />
                                </div>
                            </div>
                        </div>

                        {trigarLoader && (
                            <div style={{ fontWeight: "600", color: "green", padding: "5px" }}>
                                Please wait, recon is in progress
                            </div>
                        )}
                        {trigarForErrorMessage && (
                            <div style={{ color: "red", fontWeight: "700" }}>No Records Present!</div>
                        )}
                    </Modal.Body>

                    <Modal.Footer style={{ padding: "12px 22px", gap: "8px", borderTop: `1px solid ${C.borderLight}` }}>
                        <button
                            type="button"
                            onClick={handleOpenAndCloseModel}
                            style={{ padding: "0 18px", height: "36px", borderRadius: "8px", border: `1px solid ${C.border}`, background: C.white, fontSize: "13px", color: C.textSub, cursor: "pointer" }}
                        >
                            Cancel
                        </button>
                        <LoadingButton
                            type="submit"
                            size="small"
                            loading={loaderSubmitButton}
                            variant="contained"
                            style={{ height: "36px", minWidth: "140px", borderRadius: "8px", background: C.navy, fontSize: "13px", fontWeight: "600", textTransform: "none", boxShadow: "none" }}
                        >
                            Generate Report
                        </LoadingButton>
                    </Modal.Footer>
                </ValidatorForm>
            </Modal>

            {/* ── Snackbar ── */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                style={{ maxWidth: "340px" }}
            >
                <MuiAlert elevation={6} variant="filled" onClose={handleSnackbarClose} severity={snackbarSeverity}>
                    {snackbarMessage}
                </MuiAlert>
            </Snackbar>

        </React.Fragment>
    );
}